import { useCallback, useEffect, useState } from "react"

import { supabase } from "../supabaseClient"
import { useOrganization } from "../OrganizationContext"
import { useUser } from "../UserContext"

export default function useGa4Property(propertyId) {
  const user = useUser()
  const userId = user?.id || null
  const { selectedOrgId } = useOrganization()

  const [property, setProperty] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchProperty = useCallback(async () => {
    if (!propertyId || !userId) {
      setProperty(null)
      setError(null)
      setLoading(false)
      return null
    }

    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from("ga4_properties")
        .select(
          "id, property_id, property_name, template_id, account_id, organization_id, user_id, does_not_use_google_ads",
        )
        .eq("property_id", propertyId)
        .maybeSingle()

      if (selectedOrgId) query = query.eq("organization_id", selectedOrgId)
      else query = query.eq("user_id", userId)

      const { data, error: queryError } = await query
      if (queryError) throw queryError

      setProperty(data ?? null)
      return data ?? null
    } catch (err) {
      console.error("Failed to fetch GA4 property", err)
      setProperty(null)
      setError(err)
      return null
    } finally {
      setLoading(false)
    }
  }, [propertyId, selectedOrgId, userId])

  useEffect(() => {
    if (!propertyId) {
      setProperty(null)
      setError(null)
      setLoading(false)
      return
    }

    if (!userId) {
      setProperty(null)
      setError(null)
      setLoading(true)
      return
    }

    setProperty(null)
    setError(null)
    fetchProperty()
  }, [fetchProperty, propertyId, userId])

  const refresh = useCallback(() => {
    if (!propertyId || !userId) {
      return Promise.resolve(null)
    }

    return fetchProperty()
  }, [fetchProperty, propertyId, userId])

  return { property, loading, error, refresh }
}

